import React, {useEffect} from 'react'
import {observer} from 'mobx-react'
import {SubmitHandler, UseFormReturn} from 'react-hook-form'
import {Field} from '../../../components/field'
import {CheckIcon, CloseIcon} from '../../../components/icons'
import {useCheckPostcodeFields} from '../../../hooks/forms/useCheckPostcodeFields'
import {useBasketStore} from '../../../store/hooks/useStore'
import {Box, Button, Flex, Stack, Text} from '../../../vanilla'
import * as styles from '../styles.css'
import {CheckPostalCodeFormFields} from '../../../types/forms'
import useDeliveryBooking from '../../../hooks/use-delivery-booking'

interface EditPostCodeProps {
  form: UseFormReturn<CheckPostalCodeFormFields>
  setPostalCode: (postcode: string) => void
  checkDeliveryValidity: ReturnType<typeof useDeliveryBooking>['checkDeliveryValidity']
  setPostCodeAvailability: ReturnType<typeof useDeliveryBooking>['setPostCodeAvailability']
  postCodeAvailability: ReturnType<typeof useDeliveryBooking>['postCodeAvailability']
}

export const EditPostCode = observer((props: EditPostCodeProps) => {
  const {
    form,
    setPostalCode,
    checkDeliveryValidity,
    postCodeAvailability,
    setPostCodeAvailability,
  } = props

  const {deliveryPostalCode} = useBasketStore()

  const fields = useCheckPostcodeFields({
    form,
    validatePostcode: async (value) => {
      const {postcode} = fields
      const {formState} = form || {}

      await form?.trigger()

      if (formState?.errors && formState.errors[postcode.name as keyof CheckPostalCodeFormFields]) {
        setPostCodeAvailability(undefined)
        return
      }

      if (value) {
        await checkDeliveryValidity(value)
      }
    },
  })

  useEffect(() => {
    form.watch('postcode')
  }, [])

  useEffect(() => {
    if (deliveryPostalCode) {
      form.setValue('postcode', deliveryPostalCode)
    }
  }, [deliveryPostalCode])

  const onSubmit: SubmitHandler<CheckPostalCodeFormFields> = async ({postcode}) => {
    await setPostalCode(postcode)
  }

  return (
    <Stack paddingX="0px" paddingTop="16px" paddingBottom="28px">
      <Text variant="text4">
        Please enter your postcode below to check if we are able to deliver to your address.
      </Text>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack>
          <Stack spacing="4px">
            <Box className={styles.postalCodeInput}>
              <Field {...fields.postcode} />
            </Box>
            {postCodeAvailability && (
              <Box marginX="16px">
                {postCodeAvailability === 'available' ? (
                  <Flex align="center" gap="4px">
                    <CheckIcon color="accent1" width="16px" height="16px" />
                    <Text variant="text4">Online delivery available</Text>
                  </Flex>
                ) : postCodeAvailability === 'unavailable' ? (
                  <Box>
                    <Flex align="center" gap="4px">
                      <CloseIcon color="error" width="16px" height="16px" />
                      <Text variant="text4">Online delivery unavailable</Text>
                    </Flex>
                    <Text paddingY="8px" variant="text2" style={{color: '#bebebe'}}>
                      We don't deliver to your area yet. Continue to register your interest and
                      we'll be in touch if our service comes to your area.
                    </Text>
                  </Box>
                ) : null}
                <Flex align="center" gap="4px">
                  <CheckIcon color="accent1" width="16px" height="16px" />
                  <Text variant="text4">Join or add a Bonus Card</Text>
                </Flex>
              </Box>
            )}
          </Stack>
          <Box marginY="24px" marginX="16px">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setPostCodeAvailability(undefined)
              }}
            >
              Edit Postcode
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  )
})
